import {
  Component,
  computed,
  ElementRef,
  inject,
  model,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '@core/diamond/service/layout.service';
import { AppBreadcrumb } from './app.breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { RippleModule } from 'primeng/ripple';
import { BadgeModule } from 'primeng/badge';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AvatarModule } from 'primeng/avatar';
import { Store } from '@ngrx/store';
import {
  selectCanChangeBetweenModules,
  selectProfile,
  selectSelectedCompany,
  SessionState,
} from '@app/core/state/session';
import { AuthActions, AuthState } from '@app/core/state/auth';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Profile } from '@core/domain/models';
import { EventTrackerDirective } from '@app/core/directives/event-tracker.directive';
import { DialogModule } from 'primeng/dialog';
import { ChangePasswordComponent } from '@shared/components/change-password/change-password.component';
import { CompanySelectorComponent } from '@app/core/components/company-selector/company-selector.component';

@Component({
  selector: '[app-topbar]',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    StyleClassModule,
    AppBreadcrumb,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    RippleModule,
    BadgeModule,
    OverlayBadgeModule,
    AvatarModule,
    EventTrackerDirective,
    TranslatePipe,
    DialogModule,
    ChangePasswordComponent,
    CompanySelectorComponent,
  ],
  template: `<div class="layout-topbar">
      <div class="topbar-left xflex-col xmd:flex-row xitems-start">
        <a
          tabindex="0"
          #menubutton
          type="button"
          class="menu-button self-start"
          (click)="onMenuButtonClick()"
        >
          <i class="pi pi-chevron-left"></i>
        </a>
        <img class="horizontal-logo" src="/layout/images/logo-white.svg" alt="diamond-layout" />
        <span class="topbar-separator"></span>
        <div app-breadcrumb></div>
      </div>

      <div class="topbar-right">
        <ul class="topbar-menu">
          @if (isCustomerModule() && (selectedCompany$ | async)?.logoUrl) {
            <li class="right-sidebar-item">
              <img
                [src]="(selectedCompany$ | async).logoUrl"
                [alt]="(selectedCompany$ | async).name"
                class="hidden lg:block"
                style="max-width: 100%; max-height: 50px; width: auto; height: auto; object-fit: contain"
              />
            </li>
          }
          @if (showCompanySelector()) {
            <li class="right-sidebar-item">
              <app-company-selector></app-company-selector>
            </li>
          }
          <li class="profile-item static sm:relative">
            <a
              class="right-sidebar-button relative z-50 gap-2"
              pStyleClass="@next"
              enterFromClass="hidden"
              enterActiveClass="animate-scalein"
              leaveActiveClass="animate-fadeout"
              leaveToClass="hidden"
              [hideOnOutsideClick]="true"
            >
              <p-avatar
                [label]="
                  userProfile.urlProfileImage === null ? userProfile.name.substring(0, 1) : null
                "
                size="large"
                shape="circle"
                [image]="userProfile.urlProfileImage"
              ></p-avatar>
              <span class="flex flex-col leading-none">
                <span class="text-sm font-semibold">{{ userProfile.name }}</span>
                <span class="text-xs text-gray-500">{{ userProfile.surname }}</span>
              </span>
            </a>
            <div
              class="list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-60 mt-2 right-0 z-999 top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
            >
              <ul class="flex flex-col gap-1">
                <li>
                  <a
                    [appEventTracker]="{ action: 'change_of_visual_ui' }"
                    (click)="onConfigButtonClick()"
                    class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                  >
                    <i class="pi pi-cog"></i>
                    <span>
                      {{ 'core.mirage.topbar.config' | translate }}
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    (click)="onChangePassword($event)"
                    class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                  >
                    <i class="pi pi-key"></i>
                    <span>{{ 'core.mirage.topbar.change_password' | translate }}</span>
                  </a>
                </li>
                @if (canChangeModules) {
                  <li>
                    <a
                      (click)="onChangeOfModule()"
                      class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                    >
                      <i class="pi pi-arrow-right"></i>
                      <span>{{ getLabelOfChangeModule() }}</span>
                    </a>
                  </li>
                }
                <li>
                  <a
                    [appEventTracker]="{ action: 'logout' }"
                    (click)="onSignOut()"
                    class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                  >
                    <i class="pi pi-power-off"></i>
                    <span>{{ 'core.mirage.topbar.sign_out' | translate }}</span>
                  </a>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <p-dialog
      [(visible)]="showChangePwd"
      [breakpoints]="{ '960px': '75vw', '640px': '100vw' }"
      [style]="{ width: '20vw' }"
      header="{{ 'core.mirage.topbar.change_password' | translate }}"
      [modal]="true"
    >
      <ng-template #content>
        <app-change-password
          (passwordChanged)="showChangePwd = false"
          (cancel)="showChangePwd = false"
        ></app-change-password>
      </ng-template>
    </p-dialog>`,
})
export class AppTopbar implements OnInit {
  layoutService = inject(LayoutService);

  isDarkTheme = computed(() => this.layoutService.isDarkTheme());

  @ViewChild('menubutton') menuButton!: ElementRef;

  showChangePwd: boolean = false;
  userProfile: Profile;
  canChangeModules: boolean = false;
  selectedCompany$ = this.store.select(selectSelectedCompany);

  constructor(
    private router: Router,
    private store: Store<SessionState>,
    private storeAuth: Store<AuthState>,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.store.select(selectProfile).subscribe((data) => (this.userProfile = data));
    this.store
      .select(selectCanChangeBetweenModules)
      .subscribe((data) => (this.canChangeModules = data));
  }

  onMenuButtonClick() {
    if (this.layoutService.isHorizontal()) {
      this.layoutService.layoutState.update((value) => ({
        ...value,
        staticMenuMobileActive: !value.staticMenuMobileActive,
      }));
      return;
    }

    this.layoutService.onMenuToggle();
  }

  //   showRightMenu() {
  //     this.layoutService.toggleRightMenu();
  //   }

  onConfigButtonClick() {
    this.layoutService.showConfigSidebar();
  }

  //   toggleSearchBar() {
  //     this.layoutService.layoutState.update((value) => ({
  //       ...value,
  //       searchBarActive: !value.searchBarActive,
  //     }));
  //   }

  onSignOut() {
    this.storeAuth.dispatch(AuthActions.logout());
  }

  onChangePassword(e) {
    this.showChangePwd = true;
  }

  getLabelOfChangeModule(): string {
    return this.router.url.startsWith('/customer')
      ? this.translate.instant('core.mirage.topbar.change_to_self_management')
      : this.translate.instant('core.mirage.topbar.change_to_admin_management');
  }

  onChangeOfModule() {
    this.router.navigate([
      `/${this.router.url.startsWith('/customer') ? 'employee' : 'customer'}/home`,
    ]);
  }

  showCompanySelector() {
    return this.router.url.startsWith('/customer');
  }

  isCustomerModule(): boolean {
    return this.router.url.startsWith('/customer');
  }
}
